/**
 * TimelineBar — visual day timeline showing existing entries and a ghost for new entry.
 * This component is entirely optional. To remove it, just delete the import and
 * <TimelineBar /> usage from TimeTrackingPopup. No other code depends on it.
 */

import { timeDiffMinutes } from "@/lib/format";

interface TimeBlock {
  from: string; // HH:MM
  to: string;   // HH:MM
}

interface TimelineBarProps {
  entries: TimeBlock[];
  newFrom?: string;
  newTo?: string;
}

const DAY_START = 6 * 60;  // 06:00
const DAY_END = 24 * 60;   // 24:00 (midnatt)
const TOTAL = DAY_END - DAY_START;
const HOURS = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24];

function toPercent(time: string): number {
  if (!time) return 0;
  const [h, m] = time.split(":").map(Number);
  const mins = h * 60 + m;
  return Math.max(0, Math.min(100, ((mins - DAY_START) / TOTAL) * 100));
}

export default function TimelineBar({ entries, newFrom, newTo }: TimelineBarProps) {
  const hasNew = newFrom && newTo && timeDiffMinutes(newFrom, newTo) > 0;

  return (
    <div className="px-4 py-2.5 border-b bg-muted/30">
      <div className="relative h-6 bg-muted rounded overflow-hidden">
        {/* Hour markers */}
        {HOURS.map((h) => (
          <div
            key={h}
            className="absolute top-0 bottom-0 border-l border-border/40"
            style={{ left: `${toPercent(`${String(h).padStart(2, "0")}:00`)}%` }}
          >
            <span className="absolute top-0 text-[7px] text-muted-foreground ml-0.5 leading-none">
              {h}
            </span>
          </div>
        ))}

        {/* Existing entries (green blocks) */}
        {entries.map((e, i) => {
          const left = toPercent(e.from);
          const width = toPercent(e.to) - left;
          return (
            <div
              key={i}
              className="absolute top-1 bottom-1 rounded-sm bg-green-500/60"
              style={{ left: `${left}%`, width: `${Math.max(0.5, width)}%` }}
              title={`${e.from}–${e.to}`}
            />
          );
        })}

        {/* New entry ghost (blue dashed) */}
        {hasNew && (
          <div
            className="absolute top-0.5 bottom-0.5 rounded-sm border-2 border-blue-500 border-dashed bg-blue-500/15"
            style={{
              left: `${toPercent(newFrom!)}%`,
              width: `${Math.max(1, toPercent(newTo!) - toPercent(newFrom!))}%`,
            }}
          />
        )}
      </div>
    </div>
  );
}
